import type { FC, ReactNode } from 'react'

import {
    Clock,
    Mail,
    MapPin,
    MessageCircle,
    Phone
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { siteConfig } from '@/constants/site'

interface ContactDetailsProps {
    tone?: 'light' | 'dark'
    className?: string
}

export const hasContactDetails = (): boolean => {
    return Object.values(siteConfig.contact).some(value => Boolean(value?.trim()))
}

export const ContactDetails: FC<ContactDetailsProps> = ({ tone = 'light', className }): ReactNode => {
    const { phone, whatsapp, email, address, hours } = siteConfig.contact

    if (!hasContactDetails()) return null

    const isDark = tone === 'dark'
    const iconClass = cn('mt-0.5 size-4 shrink-0', isDark ? 'text-gold' : 'text-bronze')
    const linkClass = cn('rounded-sm underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2', isDark ? 'focus-visible:outline-gold' : 'focus-visible:outline-ring')

    return (
        <ul className={cn('space-y-3 text-sm', isDark ? 'text-background/80' : 'text-foreground', className)}>
            {phone && (
                <li className='flex items-start gap-3'>
                    <Phone className={iconClass} aria-hidden='true' />
                    <a href={`tel:${phone.replace(/[^+\d]/g, '')}`} className={linkClass}>
                        {phone}
                    </a>
                </li>
            )}

            {whatsapp && (
                <li className='flex items-start gap-3'>
                    <MessageCircle className={iconClass} aria-hidden='true' />
                    <a
                        href={`https://wa.me/${whatsapp.replace(/\D/g, '')}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={linkClass}
                    >
                        WhatsApp {whatsapp}
                    </a>
                </li>
            )}

            {email && (
                <li className='flex items-start gap-3'>
                    <Mail className={iconClass} aria-hidden='true' />
                    <a href={`mailto:${email}`} className={cn(linkClass, 'break-all')}>
                        {email}
                    </a>
                </li>
            )}

            {address && (
                <li className='flex items-start gap-3'>
                    <MapPin className={iconClass} aria-hidden='true' />
                    <span className='whitespace-pre-line'>
                        {address}
                    </span>
                </li>
            )}

            {hours && (
                <li className='flex items-start gap-3'>
                    <Clock className={iconClass} aria-hidden='true' />
                    <span>
                        {hours}
                    </span>
                </li>
            )}
        </ul>
    )
}
